import React from 'react';
import { IoMdClose } from 'react-icons/io';
import CartContents from '../Cart/CartContents';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const CartDrawer = ({ drawerOpen, toggleCartDrawer }) => {
  const navigate = useNavigate();
  const { user, guestId } = useSelector((state) => state.auth);
  const { cart } = useSelector((state) => state.cart);
  const userId = user ? user._id : null;

  const handleCheckout = () => {
    toggleCartDrawer();
    if (!user) {
      navigate("/login?redirect=checkout");
    } else {
      navigate("/checkout");
    }
  };

  const totalItems = cart?.products?.reduce((t, p) => t + (Number(p?.quantity) || 0), 0) || 0;

  return (
    <>
      <style>{drawerStyles}</style>

      {/* Backdrop */}
      {drawerOpen && (
        <div className="cd-backdrop" onClick={toggleCartDrawer} />
      )}

      <div className={`cd-drawer ${drawerOpen ? 'cd-drawer--open' : ''}`}>

        {/* ── HEADER ── */}
        <div className="cd-header">
          <div className="cd-header-left">
            <span className="cd-eyebrow">Your Cart</span>
            {totalItems > 0 && (
              <span className="cd-item-count">{totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
            )}
          </div>
          <button className="cd-close" onClick={toggleCartDrawer} aria-label="Close cart">
            <IoMdClose size={20} />
          </button>
        </div>

        <div className="cd-rule"><hr /></div>

        {/* ── CART BODY ── */}
        <div className="cd-body">
          {cart && cart?.products?.length > 0 ? (
            <CartContents cart={cart} userId={userId} guestId={guestId} />
          ) : (
            <div className="cd-empty">
              <div className="cd-empty-icon">
                <svg width="36" height="36" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <span className="cd-empty-title">Your cart is empty</span>
              <span className="cd-empty-sub">New looks are waiting — let's fill this up.</span>
              <button className="cd-empty-cta" onClick={toggleCartDrawer}>
                Continue Shopping
              </button>
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        {cart && cart?.products?.length > 0 && (
          <div className="cd-footer">
            <div className="cd-rule cd-rule--footer"><hr /></div>
            <button onClick={handleCheckout} className="cd-checkout-btn">
              Proceed to Checkout
            </button>
            <p className="cd-footer-note">
              Shipping, taxes, and discount codes calculated at checkout.
            </p>
          </div>
        )}

      </div>
    </>
  );
};

const drawerStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  .cd-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.35);
    z-index: 49;
    animation: cd-fade-in 0.25s ease;
  }
  @keyframes cd-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .cd-drawer {
    position: fixed;
    top: 0; right: 0;
    width: 100%;
    max-width: 420px;
    height: 100%;
    background: #f8f8f8;
    z-index: 50;
    display: flex;
    flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-family: 'Exo 2', sans-serif;
  }
  .cd-drawer--open { transform: translateX(0); }

  /* ── HEADER ── */
  .cd-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 24px 20px;
    flex-shrink: 0;
  }

  .cd-header-left {
    display: flex;
    align-items: baseline;
    gap: 12px;
  }

  .cd-eyebrow {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: #000;
  }

  .cd-item-count {
    font-size: 9px;
    font-weight: 300;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: black;
  }

  .cd-close {
    background: transparent;
    border: 1px solid black;
    color: black;
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  .cd-close:hover {
    background: #000;
    color: #fff;
    border-color: #000;
  }

  /* ── RULE ── */
  .cd-rule { padding: 0 24px; flex-shrink: 0; }
  .cd-rule hr {
    border: none;
    border-top: 1px solid rgba(0,0,0,0.08);
    margin: 0;
  }
  .cd-rule--footer { padding: 0; }

  /* ── BODY ── */
  .cd-body {
    flex: 1;
    overflow-y: auto;
    padding: 0 24px;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .cd-body::-webkit-scrollbar { display: none; }

  /* ── EMPTY ── */
  .cd-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 12px;
    padding: 40px 0;
    text-align: center;
  }

  .cd-empty-icon {
    color: black);
    margin-bottom: 8px;
  }

  .cd-empty-title {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: black;
  }

  .cd-empty-sub {
    font-size: 10px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
    max-width: 220px;
    line-height: 1.8;
  }

  .cd-empty-cta {
    margin-top: 16px;
    background: #000;
    color: #fff;
    border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    padding: 12px 28px;
    cursor: pointer;
    transition: background 0.2s ease;
  }
  .cd-empty-cta:hover { background: #222; }

  /* ── FOOTER ── */
  .cd-footer {
    flex-shrink: 0;
    padding: 16px 24px 24px;
    background: #f8f8f8;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .cd-checkout-btn {
    width: 100%;
    background: #000;
    color: #fff;
    border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    padding: 16px;
    cursor: pointer;
    transition: background 0.2s ease;
  }
  .cd-checkout-btn:hover { background: #222; }

  .cd-footer-note {
    font-size: 8px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
    text-align: center;
    line-height: 1.8;
    text-transform: uppercase;
  }

  @media (max-width: 480px) {
    .cd-drawer { max-width: 100%; }
  }
`;

export default CartDrawer;
